import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { getBrandVoiceTemplates, getBrandVoice, saveBrandVoice } from '../../services/configService';

export default function BrandVoice() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      getBrandVoiceTemplates(),
      getBrandVoice(),
    ])
      .then(([tmpls, config]) => {
        setTemplates(tmpls);
        const current = config.resolvedTemplate?.id ?? null;
        setSelected(current);
        setSaved(current);
      })
      .catch(() => setError('Failed to load brand voice settings.'))
      .finally(() => setLoading(false));
  }, [user]);

  const preview = templates.find(t => t.id === selected)?.preview;
  const isDirty = selected !== saved;

  async function handleSave() {
    if (!selected) return;
    setError('');
    setSaving(true);
    try {
      await saveBrandVoice(selected);
      setSaved(selected);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#475569', fontSize: 13 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ fontSize: 13, color: '#64748b' }}>Choose how your AI communicates with customers.</p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {templates.map(({ id, name }) => (
          <motion.button key={id} onClick={() => setSelected(id)}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '8px 20px', borderRadius: 999, fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
              border: selected === id ? '1px solid #22D3EE' : '1px solid rgba(71,158,195,0.15)',
              background: selected === id ? 'linear-gradient(135deg,rgba(71,158,195,0.2),rgba(34,211,238,0.2))' : 'rgba(15,23,42,0.5)',
              color: selected === id ? '#22D3EE' : '#64748b',
              transition: 'all .18s',
            }}
          >{name}</motion.button>
        ))}
      </div>

      {selected && (
        <AnimatePresence mode="wait">
          <motion.div key={selected}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            style={{
              padding: '14px 18px', borderRadius: 12,
              background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)',
            }}
          >
            <p style={{ fontSize: 12, color: '#475569', marginBottom: 6, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Preview</p>
            <p style={{ fontSize: 13.5, color: '#a5f3fc', lineHeight: 1.65, margin: 0 }}>"{preview}"</p>
          </motion.div>
        </AnimatePresence>
      )}

      {error && (
        <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!isDirty || saving || !selected}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 9, border: 'none',
            cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
            background: !isDirty || saving ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
            color: !isDirty || saving ? '#334155' : '#fff',
            fontSize: 14, fontWeight: 700,
            opacity: saving ? 0.7 : 1,
            transition: 'opacity .2s, background .2s',
          }}
        >
          {saving ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                style={{ animation: 'spin 0.8s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
              </svg>
              Saving…
            </>
          ) : 'Save'}
        </button>

        <AnimatePresence>
          {!isDirty && saved && (
            <motion.span
              initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ fontSize: 13, color: '#10b981', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
